// Performance mode ("fx-lite"): automatically strips the GPU-heavy effects
// (backdrop-filter blur, Ken Burns background, ember particles, glow pulses,
// 3D map tile transforms) on devices whose compositor glitches with them.
//
// Why: Samsung Galaxy Z Fold / Flip (Chrome & Samsung Internet) have a known
// compositing bug — pages stacking backdrop-filter layers with transform-
// accelerated content flash black and tear, worst in tablet mode where the
// screen is huge and the existing `@media (hover:none), (max-width:820px)`
// blur-kill no longer applies (the Fold reports hover:hover at ~884px wide).
// Nobody should ever flip a switch for the site to work: this is 100% automatic
// and re-checked live on fold/unfold (resize/orientationchange).
//
// Detection (any hit → lite):
//   1. Samsung foldable model number in the UA (SM-F9xx Fold, SM-F7xx Flip)
//   2. Viewport Segments API reporting an actual fold/hinge
//   3. Large Android touchscreen (foldables in tablet mode, future models)

const FOLDABLE_RE = /SM-F9\d{2,3}|SM-F7\d{2,3}/i;

function needsLite() {
    const ua = navigator.userAgent || "";
    if (FOLDABLE_RE.test(ua)) return true;
    try {
        if ((window.visualViewport?.segments || []).length > 1) return true;
    } catch { /* older browsers */ }
    const isAndroid = /Android/i.test(ua);
    const touch = (navigator.maxTouchPoints || 0) > 0;
    const smallSide = Math.min(window.innerWidth, window.innerHeight);
    return isAndroid && touch && smallSide > 820;
}

// Read the current mode (initPerfMode runs before first render, so this is
// safe to call during render). Map components use it to pick non-animated
// Leaflet options on foldables.
export function isLiteMode() {
    return document.documentElement.classList.contains("fx-lite");
}

// Leaflet option sets that avoid the GPU-composited animation paths on
// foldables (zoom/fade animations flash black there). Empty on normal devices.
export function liteMapContainerProps() {
    return isLiteMode()
        ? { zoomAnimation: false, fadeAnimation: false, markerZoomAnimation: false }
        : {};
}

export function liteTileLayerProps() {
    return isLiteMode()
        ? { updateWhenIdle: true, updateWhenZooming: false, keepBuffer: 4 }
        : {};
}

// Called once from index.js before first render.
export function initPerfMode() {
    const apply = () => {
        document.documentElement.classList.toggle("fx-lite", needsLite());
    };
    apply();
    window.addEventListener("resize", apply);
    window.addEventListener("orientationchange", apply);
}
